"use client";

import { useEffect, useRef } from "react";
import { useLocale } from "next-intl";
import { useAccount, useCart, useWishlist } from "frontastic";

/**
 * Jerly Publisher - reads data from providers and publishes it to the Jerly Widget.
 * This component is rendered inside Providers (has access to hooks)
 * and communicates with JerlyWidget (in layout) via custom events.
 *
 * Pattern: Publisher-Listener (B2C Version)
 * - Publisher: This component (inside Providers, has access to hooks)
 * - Listener: JerlyWidget (in layout, listens to events)
 * - B2C Specific: Creates anonymous cart if none exists, widget always visible
 */
export function JerlyPublisher() {
  const locale = useLocale();
  const { account, loggedIn } = useAccount();
  const { data: cart, updateCart } = useCart();
  const { data: wishlist } = useWishlist();
  const cartCreationAttempted = useRef(false);

  // B2C: Create anonymous cart if no cart exists
  // This ensures the widget always has a cartId to work with
  useEffect(() => {
    if (!cart?.cartId && !cartCreationAttempted.current) {
      cartCreationAttempted.current = true;

      console.log(
        "COMMERCETOOLS 🛒 Publisher: No hay carrito, creando carrito anónimo para B2C..."
      );

      // updateCart with empty body creates an anonymous cart
      updateCart({})
        .then((createdCart) => {
          console.log(
            "COMMERCETOOLS ✅ Publisher: Carrito anónimo creado exitosamente:",
            {
              cartId: createdCart.cartId,
              timestamp: new Date().toISOString(),
            }
          );
        })
        .catch((error) => {
          console.error(
            "COMMERCETOOLS ❌ Publisher: Error al crear carrito anónimo:",
            error
          );
          // Reset flag to allow retry
          cartCreationAttempted.current = false;
        });
    }
  }, [cart?.cartId, updateCart]);

  // Extract widget data
  const userId = loggedIn && account?.accountId ? account.accountId : undefined;
  const cartId = cart?.cartId || undefined;
  const wishlistId = wishlist?.wishlistId || undefined;

  // Publish data when it changes
  // B2C: Only publish when we have a cartId (wait for cart creation to complete)
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Don't publish until we have a cartId (required for B2C)
    if (!cartId) {
      console.log(
        "COMMERCETOOLS ⏳ Publisher: Esperando creación de carrito antes de publicar..."
      );
      return;
    }

    const data = {
      userId,
      cartId,
      wishlistId,
      locale,
    };

    console.log("COMMERCETOOLS 📤 Publisher: Publicando datos al widget:", {
      userId: userId || "(anónimo)",
      cartId: cartId,
      wishlistId: wishlistId || "(ninguno)",
      timestamp: new Date().toISOString(),
    });

    window.dispatchEvent(
      new CustomEvent("widget-data-changed", { detail: data })
    );
  }, [userId, cartId, wishlistId, locale]);

  // This component doesn't render anything
  return null;
}